import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { reviews } from '@/mocks/reviews';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于 17c.com',
  description: '了解 17c.com 的品牌故事、核心团队、用户口碑与联系方式。',
  url: 'https://17c.com/about',
};

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '17c.com',
  url: 'https://17c.com/',
  description: '17c.com 是您值得信赖的在线平台，提供丰富的内容与服务资源。',
  foundingDate: '2020',
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'support@17c.com',
    telephone: '+86-400-123-4567',
    contactType: 'customer service',
    availableLanguage: 'Chinese',
  },
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      if (honeypotEl) {
        formData.delete('phone_alt');
      }

      const response = await fetch('https://readdy.ai/api/form/d9phd0pn2trhb35gvfeg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // not json
      }

      const code = (parsed as Record<string, string>).code;
      const serverMsg = (parsed as { meta?: { message?: string; detail?: string }; message?: string }).meta?.message
        || (parsed as { message?: string }).message
        || (parsed as { meta?: { detail?: string } }).meta?.detail
        || responseText;

      if (response.ok && code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 17c.com"
        description="了解 17c.com 的品牌故事、核心价值、用户口碑与联系方式。17c.com 是您值得信赖的在线内容平台。"
        keywords="17c.com,关于17c.com,17c.com品牌,17c.com联系方式"
        canonicalUrl="https://17c.com/about"
        schema={{ ...aboutSchema, ...orgSchema }}
      />

      <div className="pt-20">
        <Breadcrumb items={[{ label: '关于我们' }]} />

        {/* Brand Intro */}
        <section className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">
                关于 17c.com
              </h1>
              <p className="text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                17c.com 成立于 2020 年，致力于打造一个值得信赖的在线内容平台。我们汇聚优质资源，为用户提供丰富的内容与便捷的服务体验。
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
              <div className="p-6 rounded-lg bg-background-100">
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">我们的使命</h2>
                <p className="text-foreground-600 leading-relaxed text-sm">
                  以用户为中心，构建一个开放、安全、高效的内容生态。17c.com 的使命是让每一位用户都能便捷地获取所需信息，在数字时代保持竞争力。我们相信，优质的内容应当触手可及。
                </p>
              </div>
              <div className="p-6 rounded-lg bg-background-100">
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">核心价值</h2>
                <ul className="flex flex-col gap-3">
                  {[
                    { icon: 'ri-shield-check-line', text: '安全可靠：多层防护机制，严格保护用户数据' },
                    { icon: 'ri-lightbulb-line', text: '创新驱动：持续迭代产品，追求卓越体验' },
                    { icon: 'ri-user-heart-line', text: '用户至上：倾听用户声音，不断优化服务' },
                    { icon: 'ri-global-line', text: '开放包容：拥抱多元内容，连接广泛用户' },
                  ].map((item) => (
                    <li key={item.text} className="flex items-start gap-3 text-sm text-foreground-600">
                      <div className="w-6 h-6 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className={`${item.icon} text-xs text-accent-700`}></i>
                      </div>
                      <span>{item.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="p-6 rounded-lg bg-background-100 mb-12">
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">品牌信誉</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {[
                  { number: '500万+', label: '累计注册用户' },
                  { number: '99.9%', label: '服务可用率' },
                  { number: '4.8/5', label: '用户满意度评分' },
                  { number: '200+', label: '合作伙伴' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center p-4">
                    <p className="font-heading text-2xl md:text-3xl font-bold text-primary-600 mb-1">{stat.number}</p>
                    <p className="text-sm text-foreground-500">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-lg overflow-hidden mb-8">
              <img
                src="https://readdy.ai/api/search-image?query=A%20modern%20open%20office%20workspace%20with%20diverse%20team%20collaborating%20warm%20ambient%20lighting%20clean%20minimalist%20design%20professional%20atmosphere%20soft%20natural%20light%20from%20large%20windows%20creative%20environment%20editorial%20photography%20harmonious%20warm%20tones&width=1200&height=500&seq=about-team-17c&orientation=landscape"
                alt="17c.com 团队"
                className="w-full h-64 md:h-80 object-cover object-top"
              />
            </div>
          </div>
        </section>

        {/* Reviews */}
        <section id="reviews" className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                用户口碑
              </h2>
              <p className="text-foreground-500 max-w-xl mx-auto">来自真实用户的声音</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {reviews.map((review) => (
                <div key={review.id} className="p-5 rounded-lg bg-background-50">
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={review.avatar}
                      alt={review.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-medium text-foreground-900 text-sm">{review.name}</p>
                      <p className="text-xs text-foreground-400">{review.role}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`ri-star-fill text-sm ${i < review.rating ? 'text-primary-500' : 'text-foreground-200'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{review.content}</p>
                  <p className="text-xs text-foreground-400 mt-3">{review.date}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section id="contact" className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16 bg-background-50">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                联系我们
              </h2>
              <p className="text-foreground-500">有任何问题或建议，欢迎随时联系我们</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              {[
                { icon: 'ri-mail-line', label: '邮箱', value: 'support@17c.com' },
                { icon: 'ri-phone-line', label: '电话', value: '400-123-4567' },
                { icon: 'ri-time-line', label: '工作时间', value: '周一至周五 9:00-18:00' },
              ].map((contact) => (
                <div key={contact.label} className="p-4 rounded-lg bg-background-100 text-center">
                  <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center mx-auto mb-3">
                    <i className={`${contact.icon} text-secondary-700`}></i>
                  </div>
                  <p className="text-xs text-foreground-400 mb-1">{contact.label}</p>
                  <p className="text-sm font-medium text-foreground-900">{contact.value}</p>
                </div>
              ))}
            </div>

            <form
              ref={formRef}
              onSubmit={handleSubmit}
              data-readdy-form
              className="p-6 rounded-lg bg-background-100"
            >
              {formStatus === 'success' && (
                <div className="mb-5 p-4 rounded-lg bg-accent-100 text-accent-700 text-sm">
                  感谢您的留言！我们会尽快与您联系。
                </div>
              )}
              {formStatus === 'error' && (
                <div className="mb-5 p-4 rounded-lg bg-primary-100 text-primary-700 text-sm">
                  {formError}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  id="contact-subject"
                  name="subject"
                  type="text"
                  className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-colors"
                  placeholder="请输入留言主题"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-colors resize-none"
                  placeholder="请输入您的留言内容（最多 500 字）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多 500 字符</p>
              </div>

              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="spam-field"
              />

              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="w-full px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 whitespace-nowrap"
              >
                {formStatus === 'loading' ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i> 提交中...
                  </span>
                ) : '发送留言'}
              </button>
            </form>
          </div>
        </section>
      </div>
    </>
  );
}